// src/pages/services/TourPackages.js
import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import '../../styles/ServicePage.css';
import AnimatedBackground from '../../components/AnimatedBackground';

const TourPackages = () => {
    const fadeIn = {
        initial: { opacity: 0, y: 20 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.6 }
    };

    const testimonials = [
        {
            content: "Our Rajasthan tour was perfectly organized by Excellent Travels. From the heritage hotels to the guided tours of palaces, everything exceeded our expectations.",
            name: "Priya & Rahul Sharma",
            location: "Mumbai, India"
        },
        {
            content: "The Kerala package was incredible! The local guides were knowledgeable, and the itinerary offered the perfect balance of backwater cruises, hill stations, and cultural experiences.",
            name: "Aditya Patel",
            location: "Ahmedabad, India"
        }
    ];

    const faqs = [
        {
            question: "What's the difference between guided and independent tours?",
            answer: "Guided tours include a professional tour director who accompanies you throughout your journey, handling logistics and providing insights. Independent tours provide the flexibility to explore at your own pace with pre-arranged accommodations and transportation, but without a full-time guide."
        },
        {
            question: "How many people are typically in a tour group?",
            answer: "Group sizes vary by tour type. Our standard tours typically have 15-25 travelers, small group tours limit participation to 8-16 people, and private tours can be customized for just your travel party."
        },
        {
            question: "Are meals included in tour packages?",
            answer: "Most tour packages include daily breakfast and select lunches or dinners, particularly for special cultural experiences. The exact meal inclusions vary by tour and are clearly specified in each itinerary."
        },
        {
            question: "Can you accommodate dietary restrictions on tours?",
            answer: "Yes, we can accommodate most dietary requirements including vegetarian, vegan, gluten-free, and allergies. Please inform us of any restrictions when booking so we can make appropriate arrangements."
        }
    ];


    // Add useEffect to ensure proper scrolling
    useEffect(() => {
        // Enable scrolling when component mounts
        document.body.style.overflow = 'auto';
        document.documentElement.style.overflow = 'auto';

        // Cleanup function to ensure scrolling is enabled when component unmounts
        return () => {
            document.body.style.overflow = 'auto';
            document.documentElement.style.overflow = 'auto';
        };
    }, []);
    return (
        <motion.div
            className="service-page"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
        >
            {/* Add animated background */}
            <AnimatedBackground type="tour-packages" />

            {/* Main content */}
            <div className="service-hero">
                <img
                    src="https://images.unsplash.com/photo-1524492412937-b28074a5d7da?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80"
                    alt="Tour Packages"
                    className="service-hero-image"
                />
                <div className="service-hero-overlay">
                    <h1 className="service-title">Tour Packages</h1>
                    <p className="service-subtitle">
                        Customized tour packages for solo travelers, families, and groups.
                    </p>
                </div>
            </div>

            {/* Service Logo */}
            <img
                src="https://cdn-icons-png.flaticon.com/512/4490/4490531.png"
                alt="Tour Packages Logo"
                className="service-logo"
            />

            <div className="service-content">
                <motion.div
                    className="service-description"
                    {...fadeIn}
                >
                    <h2>
                        <img src="https://cdn-icons-png.flaticon.com/512/3127/3127995.png" alt="Experience Icon" className="section-icon" />
                        Expertly Crafted Travel Experiences
                    </h2>
                    <p>
                        Discover the world with our thoughtfully designed tour packages that combine must-see
                        attractions with authentic local experiences. At Excellent Travels, we create
                        memorable journeys that cater to your interests, preferences, and travel style.
                    </p>
                    <p>
                        Our tour packages offer:
                    </p>
                    <ul>
                        <li>Expert itinerary planning with insider access to attractions</li>
                        <li>Carefully selected accommodations that reflect local character</li>
                        <li>Professional, knowledgeable guides and tour directors</li>
                        <li>Balanced schedules with both guided activities and free time</li>
                        <li>Authentic cultural experiences and local interactions</li>
                        <li>Seamless transportation and logistics management</li>
                    </ul>
                    <p>
                        Whether you're seeking adventure, cultural immersion, relaxation, or a mix of experiences,
                        our tour packages provide the perfect framework for your dream vacation.
                    </p>

                    <h2>
                        <img src="https://cdn-icons-png.flaticon.com/512/2553/2553691.png" alt="Tour Styles Icon" className="section-icon" />
                        Tour Styles
                    </h2>
                    <p>
                        We offer various tour styles to match your preferred way of traveling:
                    </p>
                    <ul>
                        <li><strong>Guided Group Tours:</strong> Travel with like-minded explorers with the expertise of a tour director</li>
                        <li><strong>Small Group Tours:</strong> Intimate groups of 8-16 travelers for a more personalized experience</li>
                        <li><strong>Private Tours:</strong> Exclusive itineraries designed just for you and your travel companions</li>
                        <li><strong>Independent Tours:</strong> Pre-arranged accommodations and transportation with the freedom to explore on your own</li>
                        <li><strong>Family Tours:</strong> Kid-friendly activities and accommodations designed for multi-generational travel</li>
                    </ul>

                    <h2>
                        <img src="https://cdn-icons-png.flaticon.com/512/2942/2942076.png" alt="Destinations Icon" className="section-icon" />
                        Popular Indian Destinations
                    </h2>
                    <p>
                        Explore our most popular tour destinations across India:
                    </p>
                    <ul>
                        <li><strong>North India:</strong> Delhi, Agra, Jaipur (Golden Triangle), Varanasi, Amritsar, and Himachal Pradesh</li>
                        <li><strong>South India:</strong> Kerala backwaters, Mysore, Hampi, Pondicherry, and Ooty</li>
                        <li><strong>East India:</strong> Kolkata, Darjeeling, Sikkim, Sundarbans, and Odisha temples</li>
                        <li><strong>West India:</strong> Goa beaches, Mumbai, Udaipur, Jaisalmer, and Gujarat heritage sites</li>
                        <li><strong>Central India:</strong> Khajuraho, Orchha, Sanchi, and Madhya Pradesh wildlife</li>
                        <li><strong>Island Escapes:</strong> Andaman & Nicobar Islands and Lakshadweep coral paradise</li>
                    </ul>

                    {/* Feature Image */}
                    <div className="feature-image-container">
                        <img
                            src="https://images.unsplash.com/photo-1524492412937-b28074a5d7da?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80"
                            alt="India Tourism"
                            className="feature-image"
                        />
                        <div className="feature-image-caption">Experience the vibrant colors and rich culture of India</div>
                    </div>

                    <h2>
                        <img src="https://cdn-icons-png.flaticon.com/512/3774/3774278.png" alt="Packages Icon" className="section-icon" />
                        Featured Tour Packages
                    </h2>
                    <p>
                        Our specially curated Indian tour packages:
                    </p>
                    <ul>
                        <li><strong>Kerala Backwaters:</strong> Explore Kochi, Munnar, Thekkady, and Alleppey houseboat - Contact for custom pricing</li>
                        <li><strong>Rajasthan Heritage:</strong> Discover Jaipur, Jodhpur, Udaipur, Jaisalmer, and enjoy a desert safari - Contact for custom pricing</li>
                        <li><strong>Himalayan Retreat:</strong> Visit Shimla, Manali, Dharamshala, and Dalhousie - Contact for custom pricing</li>
                        <li><strong>Golden Triangle:</strong> Experience Delhi, Agra, and Jaipur with luxury accommodations - Contact for custom pricing</li>
                        <li><strong>Andaman Island Explorer:</strong> Discover Port Blair, Havelock, and Neil Island - Contact for custom pricing</li>
                        <li><strong>Spiritual Journey:</strong> Visit Varanasi, Bodhgaya, Allahabad, and Ayodhya - Contact for custom pricing</li>
                    </ul>

                    <h2>
                        <img src="https://cdn-icons-png.flaticon.com/512/2553/2553691.png" alt="Specialty Tours Icon" className="section-icon" />
                        Specialty Tours
                    </h2>
                    <p>
                        For travelers with specific interests, we offer specialized tour experiences:
                    </p>
                    <ul>
                        <li><strong>Food & Wine Tours:</strong> Culinary adventures with cooking classes and tastings</li>
                        <li><strong>Adventure Tours:</strong> Hiking, biking, and multi-sport activities</li>
                        <li><strong>Cultural Tours:</strong> Deep dives into history, art, and local traditions</li>
                        <li><strong>Wildlife Tours:</strong> Ethical encounters with animals in their natural habitats</li>
                        <li><strong>Festival Tours:</strong> Trips centered around world-famous celebrations and events</li>
                    </ul>

                    {/* Feature Image */}
                    <div className="feature-image-container">
                        <img
                            src="https://images.unsplash.com/photo-1544550581-1bcabf842b77?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1470&q=80"
                            alt="Cultural Experience"
                            className="feature-image"
                        />
                        <div className="feature-image-caption">Immerse yourself in authentic cultural experiences</div>
                    </div>
                </motion.div>

                <motion.div
                    className="service-sidebar"
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                >
                    <img
                        src="https://images.unsplash.com/photo-1526711657229-e7e080961c85?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1074&q=80"
                        alt="Tour Guide"
                        className="sidebar-image"
                    />

                    <img
                        src="https://cdn-icons-png.flaticon.com/512/4490/4490531.png"
                        alt="Tour Packages Icon"
                        className="service-sidebar-logo"
                    />

                    <h3>Why Choose Our Tour Packages</h3>
                    <ul className="service-features">
                        <li>
                            <span className="feature-icon">
                                <img src="https://cdn-icons-png.flaticon.com/512/3068/3068395.png" alt="Experience Icon" />
                            </span>
                            30+ years of tour planning expertise
                        </li>
                        <li>
                            <span className="feature-icon">
                                <img src="https://cdn-icons-png.flaticon.com/512/2553/2553691.png" alt="Immersive Icon" />
                            </span>
                            Authentic, immersive experiences
                        </li>
                        <li>
                            <span className="feature-icon">
                                <img src="https://cdn-icons-png.flaticon.com/512/2933/2933116.png" alt="Pricing Icon" />
                            </span>
                            No hidden costs or surprise fees
                        </li>
                        <li>
                            <span className="feature-icon">
                                <img src="https://cdn-icons-png.flaticon.com/512/2971/2971416.png" alt="Sustainable Icon" />
                            </span>
                            Sustainable travel practices
                        </li>
                        <li>
                            <span className="feature-icon">
                                <img src="https://cdn-icons-png.flaticon.com/512/2933/2933038.png" alt="Accommodation Icon" />
                            </span>
                            Handpicked, quality accommodations
                        </li>
                        <li>
                            <span className="feature-icon">
                                <img src="https://cdn-icons-png.flaticon.com/512/2784/2784389.png" alt="Support Icon" />
                            </span>
                            24/7 emergency assistance
                        </li>
                        <li>
                            <span className="feature-icon">
                                <img src="https://cdn-icons-png.flaticon.com/512/2921/2921222.png" alt="Booking Icon" />
                            </span>
                            Flexible booking conditions
                        </li>
                    </ul>
                </motion.div>
            </div>

            <motion.div
                className="cta-section"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
            >
                <h2>Ready to Explore India?</h2>
                <p>
                    Contact our tour specialists for customized pricing and to create the perfect package for your next adventure.
                </p>
                <Link to="/contact" className="cta-button">
                    Request a Personalized Quote
                    <span className="arrow-icon">→</span>
                </Link>
            </motion.div>

            <motion.div
                className="testimonials"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.6 }}
            >
                <h2>What Our Tour Travelers Say</h2>
                <div className="testimonials-grid">
                    {testimonials.map((testimonial, index) => (
                        <motion.div
                            key={index}
                            className="testimonial-card"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.7 + (index * 0.1) }}
                        >
                            <p className="testimonial-content">"{testimonial.content}"</p>
                            <div className="testimonial-author">
                                <div className="author-info">
                                    <h4>{testimonial.name}</h4>
                                    <p>{testimonial.location}</p>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </motion.div>

            <motion.div
                className="faq-section"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.8 }}
            >
                <h2>Frequently Asked Questions</h2>
                {faqs.map((faq, index) => (
                    <motion.div
                        key={index}
                        className="faq-item"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.9 + (index * 0.1) }}
                    >
                        <h3 className="faq-question">{faq.question}</h3>
                        <p className="faq-answer">{faq.answer}</p>
                    </motion.div>
                ))}
            </motion.div>
        </motion.div>
    );
};

export default TourPackages;